// packages/ui/src/ContentCard.tsx

import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Button, ButtonProps } from './Button';
import { Icon } from './Icon';
import { Badge } from './Badge';

// Interface for the new, optional badge prop
interface BadgeInfo {
  text: string;
  color: 'green' | 'blue' | 'purple' | 'gray' | 'yellow' | 'red';
}

const glowColorMap: Record<string, string> = {
  'emerald-500': 'rgba(16, 185, 129, 0.28)',
  'blue-500': 'rgba(59, 130, 246, 0.28)',
  'purple-500': 'rgba(168, 85, 247, 0.28)',
  'gray-500': 'rgba(100, 116, 139, 0.22)',
  'yellow-500': 'rgba(245, 158, 11, 0.28)',
  'red-500': 'rgba(239, 68, 68, 0.28)',
};

interface ContentCardProps {
  iconName?: string;
  icon?: React.ReactNode;
  title: string;
  text?: string | React.ReactNode;
  cta?: {
    text: string;
    onClick?: () => void;
    href?: string;
    variant?: ButtonProps['variant'];
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
    ariaLabel?: string;
  };
  visualContent?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  iconContainerClassName?: string;
  contentClassName?: string;
  titleClassName?: string;
  textClassName?: string;
  visualContainerClassName?: string;
  hoverEffect?: 'lift' | 'subtle-glow' | 'none';
  index?: number;
  accentColorForGlow?: string;
  badge?: BadgeInfo; // <-- THE ONLY NEW PROP ADDED
}

export const ContentCard: React.FC<ContentCardProps> = ({
  iconName,
  icon,
  title,
  text,
  cta,
  visualContent,
  className,
  style,
  iconContainerClassName,
  contentClassName,
  titleClassName,
  textClassName,
  visualContainerClassName,
  hoverEffect = 'lift',
  index = 0,
  accentColorForGlow = 'emerald-500',
  badge, // <-- Destructuring the new badge prop
}) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: index * 0.1,
        ease: 'easeOut',
      },
    },
  };
  const glowColor = glowColorMap[accentColorForGlow] ?? accentColorForGlow;

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className={clsx("relative h-full group", className)}
      style={{ ...style, ['--content-card-glow' as string]: glowColor }}
    >
      <div
        className={clsx(
          "absolute inset-0 bg-black rounded-xl",
          "transition-transform duration-150 ease-out",
          hoverEffect === 'lift' ? "group-hover:translate-x-1 group-hover:translate-y-1" : "",
          "[.theme-liquid-glass_&]:hidden"
        )}
      />
      <div
        className={clsx(
          "relative bg-white rounded-xl border-2 border-black overflow-hidden",
          "flex flex-col h-full",
          "transition-transform duration-150 ease-out",
          "[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20",
          "[.theme-liquid-glass_&]:rounded-3xl [.theme-liquid-glass_&]:backdrop-blur-xl",
          hoverEffect === 'lift' && "group-hover:-translate-x-1 group-hover:-translate-y-1",
          hoverEffect === 'lift' && "[.theme-liquid-glass_&]:group-hover:translate-x-0 [.theme-liquid-glass_&]:group-hover:-translate-y-1",
          hoverEffect === 'subtle-glow' && 'group-hover:shadow-[0_0_0_2px_var(--content-card-glow),0_18px_40px_-24px_var(--content-card-glow)]'
        )}
      >
        {/* --- NEW: Badge Rendering Logic --- */}
        {/* This is positioned absolutely within the card and does not affect the layout */}
        {badge && (
          <div className="absolute top-4 right-4 z-10">
            <Badge text={badge.text} accentColor={badge.color} />
          </div>
        )}

        {visualContent && (
          <div className={clsx("border-b-2 border-black [.theme-liquid-glass_&]:border-b [.theme-liquid-glass_&]:border-white/20", visualContainerClassName)}>
            {visualContent}
          </div>
        )}
        <div className={clsx("p-6 md:p-8 flex flex-col flex-grow", contentClassName)}>
          {(icon || iconName) && (
            <div className={clsx("mb-4", iconContainerClassName)}>
              {icon ? icon : <Icon name={iconName!} size={32} className="text-emerald-500 [.theme-liquid-glass_&]:text-violet-400" />}
            </div>
          )}
          <h3 className={clsx("text-xl font-bold text-gray-900 leading-tight [.theme-liquid-glass_&]:text-white", titleClassName)}>
            {title}
          </h3>
          {text && (
            <div className={clsx("mt-2 text-slate-600 flex-grow leading-relaxed [.theme-liquid-glass_&]:text-white/70", textClassName)}>
              {typeof text === 'string' ? <p>{text}</p> : text}
            </div>
          )}
          {cta && (
            <div className="mt-6 pt-6 border-t-2 border-slate-200 [.theme-liquid-glass_&]:border-t [.theme-liquid-glass_&]:border-white/10">
              <Button
                text={cta.text}
                onClick={cta.onClick}
                variant={cta.variant || "secondary"}
                icon={cta.icon}
                iconPosition={cta.iconPosition}
                ariaLabel={cta.ariaLabel || cta.text}
                fullWidth
              />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ContentCard;
